import { ZodError } from 'zod';
import type { AnyObject } from '$util/types';
import type {
  AnyContentComponent,
  ContentComponent,
  ContentComponentFetchServerSideData,
  ContentComponentTranslatedEntities,
} from '$cms/ContentComponent';
import type { CmsContentComponent } from '$cms/types';
import { UspBenefits } from '$quantum/molecules/UspBenefits';
import { SideBySideMediaText } from '$quantum/organisms/SideBySideMediaText';
import { SideBySideMediaAccordion } from '$quantum/organisms/SideBySideMediaAccordion';
import { Help } from '$quantum/organisms/Help';
import { CustomerCarousel } from '$quantum/organisms/CustomerCarousel';
import { ReviewsCarousel } from '$quantum/organisms/ReviewsCarousel';
import { RichTextModal } from '$quantum/molecules/RichTextModal';
import { RichTextSection } from '$quantum/molecules/RichTextSection';
import { RichTextTableModal } from '$quantum/molecules/RichTextTableModal';
import { ComparisonCards } from '$quantum/molecules/ComparisonCards';
import { OurServices } from '$quantum/organisms/OurServices';
import { Logos } from '@/components/molecules/Logos';
import { ExpertInsights } from '@/components/organisms/ExpertInsights';
import { PopularLinksList } from '@/components/organisms/PopularLinksList';
import { FaqCollection } from '@/components/organisms/FaqCollection';
import { PricingTable } from '@/components/organisms/PricingTable';
import { CarMakesList } from '@/components/molecules/CarMakesList';
import { WeHaveYourBack } from '@/components/organisms/WeHaveYourBack';
import { CalloutCard } from '@/components/organisms/CalloutCard';
import { OurServicesSection } from '@/components/organisms/OurServicesSection';
import { TopLocationAndServices } from '@/components/organisms/TopLocationAndServices';
import { HeroSection } from '@/components/organisms/HeroSection';
import { SymptomsList } from '@/components/organisms/SymptomsList';
import { CompareServices } from '@/components/organisms/CompareServices';
import { ContentComponentValidationErrorName } from './types';

/**
 * @note On NextJS Page Router (Client Components):
 * As this list grows the page performance will take a hit because all possible
 * options are imported instead of only what the page uses.
 *
 * @todo On NextJS App Router (Server Components):
 * All ContentComponents should be only Server Components and there will be
 * no extra functionality to implement to improve Client performance related to the dynamic imports.
 *
 * @deprecated map in DI container instead
 *
 * @important
 * All ContentComponents need to have a comment with the documentation in notion.
 * https://www.notion.so/fixter/0f100f92fe8a4d07965a65c0a69f95d6?v=087f078295874bcb83a9f6e27ba9adbe
 *
 * @note
 * There are constrains with the `import()` to take only a string parameter that is not computed.
 * Because we can't compute what import paths a page will need on the fly, we have a list with all the possible imports.
 */
const contentComponentImportMap = new Map<ContentComponent['displayName'], Promise<Record<string, unknown>>>([
  [HeroSection.displayName, import('@/components/organisms/HeroSection')],
  [UspBenefits.displayName, import('$quantum/molecules/UspBenefits')],
  [SideBySideMediaText.displayName, import('$quantum/organisms/SideBySideMediaText')],
  [SideBySideMediaAccordion.displayName, import('$quantum/organisms/SideBySideMediaAccordion')],
  [Help.displayName, import('$quantum/organisms/Help')],
  [CustomerCarousel.displayName, import('$quantum/organisms/CustomerCarousel')],
  [ReviewsCarousel.displayName, import('$quantum/organisms/ReviewsCarousel')],
  [RichTextModal.displayName, import('$quantum/molecules/RichTextModal')],
  [Logos.displayName, import('@/components/molecules/Logos')],
  [RichTextSection.displayName, import('$quantum/molecules/RichTextSection')],
  [RichTextTableModal.displayName, import('$quantum/molecules/RichTextTableModal')],
  [ComparisonCards.displayName, import('$quantum/molecules/ComparisonCards')],
  [PopularLinksList.displayName, import('@/components/organisms/PopularLinksList')],
  [TopLocationAndServices.displayName, import('@/components/organisms/TopLocationAndServices')],
  [ExpertInsights.displayName, import('@/components/organisms/ExpertInsights')],
  [FaqCollection.displayName, import('@/components/organisms/FaqCollection')],
  [PricingTable.displayName, import('@/components/organisms/PricingTable')],
  [CarMakesList.displayName, import('@/components/molecules/CarMakesList')],
  [WeHaveYourBack.displayName, import('@/components/organisms/WeHaveYourBack')],
  [CalloutCard.displayName, import('@/components/organisms/CalloutCard')],
  [OurServicesSection.displayName, import('@/components/organisms/OurServicesSection')],
  [SymptomsList.displayName, import('@/components/organisms/SymptomsList')],
  [CompareServices.displayName, import('@/components/organisms/CompareServices')],
  [OurServices.displayName, import('$quantum/organisms/OurServices')],
]);

/**
 * As this list grows the page performance will take a hit because all possible
 * options are imported instead of only what the page uses.
 *
 * @todo don't import all the components at once, only what the page needs.
 */
const contentComponents: AnyContentComponent[] = [
  HeroSection,
  UspBenefits,
  SideBySideMediaText,
  SideBySideMediaAccordion,
  Help,
  CustomerCarousel,
  ComparisonCards,
  ReviewsCarousel,
  RichTextModal,
  RichTextSection,
  RichTextTableModal,
  PopularLinksList,
  TopLocationAndServices,
  ExpertInsights,
  Logos,
  FaqCollection,
  PricingTable,
  CarMakesList,
  WeHaveYourBack,
  CalloutCard,
  OurServicesSection,
  SymptomsList,
  CompareServices,
  OurServices,
];

/**
 * We use Map for a performant get instead of an expensive find in array.
 */
const contentComponentMap = new Map<ContentComponent['displayName'], AnyContentComponent>(
  contentComponents.map((component) => [component.displayName, component])
);

if (contentComponentImportMap.size !== contentComponentMap.size) {
  throw new TypeError('Invalid setup for Content Components');
}

/**
 * @deprecated
 * @todo drop in favor of DI
 */
export class ContentComponentFactory {
  public readonly component: CmsContentComponent;

  public readonly UiComponent: AnyContentComponent | null;

  /**
   * Dynamically import and return the `fetchServerSideData()` of a ContentComponent if it exists,
   * returns an empty function otherwise.
   */
  static importFetchServerSideData = async (
    name: string
  ): Promise<ContentComponentFetchServerSideData<object>> => {
    const importComponent = contentComponentImportMap.get(name);
    if (!importComponent) throw TypeError(`Unknown import for ContentComponent(${name})`);
    const { fetchServerSideData = () => {} } = await importComponent;
    return fetchServerSideData as ContentComponentFetchServerSideData<object>;
  };

  /**
   * Returns the component associated with the CMS UI Component.
   */
  static getCmsUiComponent = (name: string): AnyContentComponent | null => {
    return contentComponentMap.get(name) ?? null;
  };

  constructor(component: CmsContentComponent) {
    this.component = component;
    this.UiComponent = ContentComponentFactory.getCmsUiComponent(component.uiComponent);
  }

  /**
   * Validated translated entities.
   */
  get translatedEntities(): ContentComponentTranslatedEntities {
    const entities = this.getRawTranslatedEntities();
    const schema = this.UiComponent?.schema;
    if (!schema) return {};
    try {
      return schema.translatedEntities.parse(entities);
    } catch (error) {
      if (error instanceof ZodError) error.name = ContentComponentValidationErrorName.translatedEntities;
      throw error;
    }
  }

  /**
   * Validated configuration.
   */
  get configuration(): AnyObject | undefined {
    const { configuration } = this.component;
    const schema = this.UiComponent?.schema;
    if (!schema) return undefined;
    try {
      return schema.configuration?.parse(configuration);
    } catch (error) {
      if (error instanceof ZodError) error.name = ContentComponentValidationErrorName.configuration;
      throw error;
    }
  }

  /**
   * Get the QV components props data by extracting the middle field from their keys
   *    - key for entities are in the format `{SectionName}.{field}.{variant}`
   *    - key in the format `{SectionName}.{field}{number}.{variant}` will be grouped in record props
   *
   * @todo unit test the keys transformation to props
   */
  private getRawTranslatedEntities(): ContentComponentTranslatedEntities {
    const translatedEntities = this.component.translatedEntities?.reduce((o, entity) => {
      const key = entity.key.split('.')[1];
      if (!key) {
        /**
         * The `key` type says it's only `string` but it can be `undefined` if the key is in an unexpected format.
         * @todo log error when key is missing
         */
        return o;
      }

      /**
       * key in the form `{field}{number}`.
       * eg. itemTitle1 -> { itemTitle: { 1: "title 1"} }
       */
      const matchKeyRecord = key.match(/(\w+)([0-9]+)$/);
      if (matchKeyRecord) {
        const [, k, kNo] = matchKeyRecord;
        return Object.assign(o, { [k]: Object.assign(o[k] ?? {}, { [kNo]: entity }) });
      }

      /**
       * normal key
       */
      return Object.assign(o, { [key]: entity });
    }, {} as ContentComponentTranslatedEntities);

    return translatedEntities ?? {};
  }
}
